import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import ordenesCargaCombustibleService from '../../services/ordenesCargaCombustibleService';
import {
  obtenerBorradorSolicitud, limpiarBorradorSolicitud, type OrdenSolicitudDraft,
  obtenerBorradorCarga, limpiarBorradorCarga, dataUrlAFile, type OrdenCargaDraft,
} from '../../utils/ordenCargaDraftStorage';
import { formatDateTimeSafe } from '../../utils/dateUtils';

/**
 * Si el operador quedó con una solicitud o una carga sin confirmar (perdió
 * cobertura justo al enviarla y la app se cerró o recargó antes de la
 * respuesta del servidor), esto lo detecta al volver a abrir la app y ofrece
 * reintentar sin tener que repetir el formulario ni la foto. Mismo patrón que
 * ReporteBorradorBanner.
 */
const OrdenCargaBorradorBanner: React.FC = () => {
  const { t } = useTranslation();
  const [borradorSolicitud, setBorradorSolicitud] = useState<OrdenSolicitudDraft | null>(null);
  const [borradorCarga, setBorradorCarga] = useState<OrdenCargaDraft | null>(null);
  const [reintentando, setReintentando] = useState(false);

  useEffect(() => {
    setBorradorSolicitud(obtenerBorradorSolicitud());
    setBorradorCarga(obtenerBorradorCarga());
  }, []);

  if (!borradorSolicitud && !borradorCarga) return null;

  const reintentarSolicitud = async () => {
    if (!borradorSolicitud) return;
    setReintentando(true);
    try {
      await ordenesCargaCombustibleService.solicitar(borradorSolicitud.datos);
      limpiarBorradorSolicitud();
      setBorradorSolicitud(null);
      toast.success(t('ordenes_carga.borrador.reintentado_ok'));
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || t('ordenes_carga.borrador.reintentado_error'));
    } finally {
      setReintentando(false);
    }
  };

  const reintentarCarga = async () => {
    if (!borradorCarga) return;
    setReintentando(true);
    try {
      const foto = await dataUrlAFile(borradorCarga.fotoDataUrl, borradorCarga.fotoNombre, borradorCarga.fotoTipo);
      const fotoNivel = borradorCarga.fotoNivelDataUrl
        ? await dataUrlAFile(borradorCarga.fotoNivelDataUrl, borradorCarga.fotoNivelNombre!, borradorCarga.fotoNivelTipo!)
        : undefined;
      await ordenesCargaCombustibleService.cargar(borradorCarga.ordenId, borradorCarga.destinoId, {
        cantidadCargada: borradorCarga.cantidadCargada,
        rutCargador: borradorCarga.rutCargador,
        nombreCargador: borradorCarga.nombreCargador,
        fotoGuiaDespacho: foto,
        horometroDeclarado: borradorCarga.horometroDeclarado,
        nivelDeclarado: borradorCarga.nivelDeclarado,
        fotoEvidenciaNivel: fotoNivel,
      });
      limpiarBorradorCarga();
      setBorradorCarga(null);
      toast.success(t('ordenes_carga.borrador.reintentado_ok'));
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || t('ordenes_carga.borrador.reintentado_error'));
    } finally {
      setReintentando(false);
    }
  };

  return (
    <>
      {borradorSolicitud && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-amber-500 text-white text-sm px-4 py-2 shadow-lg">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3 text-center">
            <span>⚠️ {t('ordenes_carga.borrador.solicitud_pendiente', { date: formatDateTimeSafe(borradorSolicitud.guardadoEn) })}</span>
            <div className="flex gap-2">
              <button
                onClick={reintentarSolicitud}
                disabled={reintentando}
                className="px-3 py-1 bg-white bg-opacity-20 rounded text-xs font-medium hover:bg-opacity-30 transition-colors disabled:opacity-50"
              >
                {reintentando ? t('common.loading') : t('ordenes_carga.borrador.reintentar')}
              </button>
              <button
                onClick={() => { limpiarBorradorSolicitud(); setBorradorSolicitud(null); }}
                disabled={reintentando}
                className="px-3 py-1 bg-white bg-opacity-20 rounded text-xs font-medium hover:bg-opacity-30 transition-colors disabled:opacity-50"
              >
                {t('ordenes_carga.borrador.descartar')}
              </button>
            </div>
          </div>
        </div>
      )}
      {borradorCarga && (
        <div className={`fixed left-0 right-0 z-40 bg-amber-500 text-white text-sm px-4 py-2 shadow-lg ${borradorSolicitud ? 'top-9' : 'top-0'}`}>
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3 text-center">
            <span>⚠️ {t('ordenes_carga.borrador.carga_pendiente', { date: formatDateTimeSafe(borradorCarga.guardadoEn) })}</span>
            <div className="flex gap-2">
              <button
                onClick={reintentarCarga}
                disabled={reintentando}
                className="px-3 py-1 bg-white bg-opacity-20 rounded text-xs font-medium hover:bg-opacity-30 transition-colors disabled:opacity-50"
              >
                {reintentando ? t('common.loading') : t('ordenes_carga.borrador.reintentar')}
              </button>
              <button
                onClick={() => { limpiarBorradorCarga(); setBorradorCarga(null); }}
                disabled={reintentando}
                className="px-3 py-1 bg-white bg-opacity-20 rounded text-xs font-medium hover:bg-opacity-30 transition-colors disabled:opacity-50"
              >
                {t('ordenes_carga.borrador.descartar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrdenCargaBorradorBanner;
